import { peekPropertyAssets } from '@/core/services/contentfulClient';
import { ImageSourcePropType } from 'react-native';
import {
  mapUnitDetailsDataToUi,
  mergeListingWithProjectDetails,
} from '../properties-web/services/mapUnitDetails';

/** Same width Home uses for listing cards. */
const HOME_CARD_IMAGE_WIDTH = 600;

const rememberedHeroImages = new Map<string, ImageSourcePropType[]>();

export function rememberAdhaHeroImages(
  propertyId: string,
  images: ImageSourcePropType[],
) {
  if (!propertyId || images.length === 0) {
    return;
  }
  rememberedHeroImages.set(propertyId, images);
}

export function mapAdhaUnitToProperty(
  adhaUnit: any,
  propertyId: string,
  adhaProjectDetails: any,
): any {
  const merged = mergeListingWithProjectDetails(adhaUnit, adhaProjectDetails);
  const mapped = mapUnitDetailsDataToUi(merged, propertyId);
  return {
    id: mapped.id,
    unitNumber: mapped.unitNumber,
    projectName: mapped.projectName || '',
    projectLocation: mapped.projectLocation || '',
    buildingName: '',
    handoverDate: mapped.handoverDate,
    totalPrice: mapped.totalPrice,
    preApprovalAmount: mapped.preApprovalAmount,
    paidAmount: 0,
    handoverComplete: false,
    latitude: mapped.latitude,
    longitude: mapped.longitude,
    bedrooms: mapped.bedrooms,
    bathrooms: mapped.bathrooms,
    area: mapped.areaSqFt,
    amenities: mapped.amenities || [],
    documents: mapped.documents || [],
    listingImages: adhaUnit.images || [],
    constructionCompleted: mapped.constructionCompletedPercent,
  };
}

export function mapListingImagesToSources(
  listingImages: unknown,
): ImageSourcePropType[] {
  if (!Array.isArray(listingImages) || listingImages.length === 0) {
    return [];
  }
  return listingImages.flatMap((img) => {
    if (typeof img === 'string' && img) {
      return [{ uri: img }];
    }
    if (
      img &&
      typeof img === 'object' &&
      'uri' in img &&
      typeof (img as { uri?: unknown }).uri === 'string' &&
      (img as { uri: string }).uri
    ) {
      return [{ uri: (img as { uri: string }).uri }];
    }
    return [];
  });
}

export function getAdhaHeroImages(
  propertyId?: string | null,
  projectId?: string | null,
  listingImages?: unknown,
): ImageSourcePropType[] {
  if (propertyId) {
    const remembered = rememberedHeroImages.get(propertyId);
    if (remembered && remembered.length > 0) {
      return remembered;
    }
  }
  if (projectId) {
    const cached = peekPropertyAssets(projectId, HOME_CARD_IMAGE_WIDTH);
    if (cached && cached.images.length > 0) {
      return cached.images.map((img) => ({ uri: img.url }));
    }
  }
  return mapListingImagesToSources(listingImages);
}

export function resolveAdhaDetailsRawUnit(params: Readonly<{
  propertyId: string;
  availableUnits: readonly { unit_id: string }[];
  pendingUnitReservation: {
    unitId: string;
    unit: unknown;
  } | null | undefined;
  sessionSnapshot: { unit_id?: string } | null;
}>): unknown {
  const { propertyId, availableUnits, pendingUnitReservation, sessionSnapshot } = params;

  const adhaUnit = availableUnits.find((u) => u.unit_id === propertyId);
  if (adhaUnit) {
    return adhaUnit;
  }

  if (
    pendingUnitReservation?.unitId === propertyId &&
    pendingUnitReservation.unit
  ) {
    return pendingUnitReservation.unit;
  }

  if (sessionSnapshot?.unit_id === propertyId) {
    return sessionSnapshot;
  }

  return undefined;
}

export function resolveAdhaDetailsProperty(params: Readonly<{
  propertyId: string | undefined;
  properties: ReadonlyArray<{ id: string }>;
  availableUnits: ReadonlyArray<{ unit_id: string }>;
  adhaProjectDetails: unknown;
  pendingUnitReservation: {
    unitId: string;
    unit: unknown;
  } | null | undefined;
  sessionSnapshot: { unit_id?: string } | null;
}>): any {
  const {
    propertyId,
    properties,
    availableUnits,
    adhaProjectDetails,
    pendingUnitReservation,
    sessionSnapshot,
  } = params;
  if (!propertyId) {
    return properties[0];
  }

  const owned = properties.find((p) => p.id === propertyId);
  if (owned) {
    return owned;
  }

  const rawUnit = resolveAdhaDetailsRawUnit({
    propertyId,
    availableUnits,
    pendingUnitReservation,
    sessionSnapshot,
  });
  if (rawUnit) {
    return mapAdhaUnitToProperty(rawUnit, propertyId, adhaProjectDetails);
  }

  return properties[0];
}
